import React from 'react';
import styled, { css } from 'styled-components';
import { breakpoint } from '../theme/breakpoint';
import CTA from '../cta';
import { Container } from '../styled/containers';
import generateQS from '../styled/background-image';

const imageSizes = {
  mobile: [null, 220],
  tablet: [null, 470],
};

export const ImageContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  background-size: cover;
  background-repeat: no-repeat;
  background-image: ${({ $image: image, theme }) =>
    image &&
    generateQS({
      image,
      imageSizes,
      size: 'mobile',
      background: theme.palette.darkBackground,
    })};

  ${breakpoint.tablet`
    background-image: ${({ $image: image, theme }) =>
      image &&
      generateQS({
        image,
        imageSizes,
        size: 'tablet',
        background: theme.palette.darkBackground,
      })};
  `}
`;

export const Title = styled.h1`
  font-family: ${({ theme }) => theme.fonts.condensed};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  margin-top: 0;
  margin-bottom: 0;
`;

export const ElementWrapper = styled.div``;

// helper fn to provide background image breakout
// expand div with negative margins and add same to width
const createOffset = (off) => {
  if (Array.isArray(off)) {
    return css`
      margin-left: -${off[0]}px;
      margin-right: -${off[1]}px;
      width: calc(100% + ${off[0] + off[1]}px);
    `;
  }
};

export const LeftColumn = styled.div`
  //
  // image positioning rules - alternate layout
  //
  ${({ $altLayout }) =>
    $altLayout &&
    css`
      ${ImageContainer} {
        // image column width plus 2x gutter
        width: calc(50% - 24px);
        // magic min height to show lower left quadrant image on mob
        min-height: 180px;
        // magic max-width to constrain lower left quadrant image on mob
        max-width: 200px;
        // background positioning, keep top
        background-position: right top;
        background-size: cover;

        ${createOffset([0, 60])};

        ${breakpoint.tablet`
          max-width: none;
          ${createOffset([0, 0])};
        `};

        ${breakpoint.desktop`
          ${createOffset([80, 24])};
        `};
      }
    `}

  //
  // image positioning rules - primary layout
  //
  ${({ $altLayout }) =>
    !$altLayout &&
    css`
      ${ImageContainer} {
        // magic min height to show & contrain bottom center image on mob
        min-height: 250px;
        background-size: contain;
        background-position: center bottom;

        ${breakpoint.tablet`
          min-height: 330px;
        `};

        ${breakpoint.desktop`
          max-width: 100%;
        `};
      }
    `}
`;

export const RightColumn = styled.div`
  position: relative;
  color: ${({ theme }) => theme.palette.white};

  // Right text alt column padding
  ${({ $altLayout }) =>
    $altLayout &&
    css`
      padding-left: ${({ theme }) => theme.spacing.standard};
      padding-right: ${({ theme }) => theme.spacing.standard};

      ${breakpoint.tablet`
        padding-left: ${({ theme }) => theme.spacing.large};
      `};
    `}

  ${Title} {
    ${({ $altLayout }) =>
      $altLayout &&
      css`
        // NOTE: shortcode line breaks, hidden on alt mobile
        br {
          display: none;

          ${breakpoint.tablet`
            display: block;
          `};
        }

        // h1 title rules - alternate
        text-align: left;
        font-size: 65px;
        line-height: 70px;

        ${breakpoint.desktop`
          font-size: 80px;
          line-height: 85px;
        `};
      `}

    // h1 title rules - primary
    ${({ $altLayout }) =>
      !$altLayout &&
      css`
        text-transform: uppercase;
        text-align: center;
        font-size: 85px;
        line-height: 90px;

        ${breakpoint.desktop`
          text-align: left;
          font-size: 110px;
          line-height: 115px;
        `};
      `}
  }

  ${ElementWrapper} {
    // CTA holder - ElementWrapper alternate
    ${({ $altLayout }) =>
      $altLayout &&
      css`
        text-align: right;
        margin-top: ${({ theme }) => theme.spacing.medium};

        ${breakpoint.tablet`
          text-align: left;
          margin-bottom: ${({ theme }) => theme.spacing.medium};
        `};

        ${breakpoint.desktop`
          margin-bottom: 56px;
        `};

        a {
          position: absolute;
          right: 16px;

          ${breakpoint.tablet`
            position: static;
          `};
        }
      `}

    // Brush element holder - ElementWrapper primary
    ${({ $altLayout }) =>
      !$altLayout &&
      css`
        text-align: center;
        margin-top: ${({ theme }) => theme.spacing.tiny};

        ${breakpoint.tablet`
          margin-bottom: ${({ theme }) => theme.spacing.medium};
        `};

        ${breakpoint.desktop`
          text-align: left;
          margin-bottom: ${({ theme }) => theme.spacing.standard};
          margin-left: ${({ theme }) => theme.spacing.medium};
        `};
      `}
  }
`;

/*
  Column layout grid and ordering rules
*/
export const FlexContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column-reverse;

  ${LeftColumn}, ${RightColumn} {
    display: flex;
    flex-grow: 0;
    flex-shrink: 0;
  }

  ${RightColumn} {
    flex-direction: column;
  }

  ${({ $altLayout }) =>
    !$altLayout &&
    // Left/Right Column Primary Layout: 7/12 - 5/12
    css`
      ${breakpoint.desktop`
        flex-direction: row;

        ${LeftColumn} {
          flex-basis: ${(7 / 12) * 100}%;
        }
        ${RightColumn} {
          flex-basis: ${(5 / 12) * 100}%;
        }
      `};
    `};

  ${({ $altLayout }) =>
    $altLayout &&
    // Left/Right Column Alternate Layout: 5/12 - 7/12
    css`
      ${breakpoint.tablet`
        flex-direction: row;

        ${LeftColumn} {
          flex-basis: 50%;
        }
        ${RightColumn} {
          flex-basis: 50%;
        }
      `};

      ${breakpoint.desktop`
        ${LeftColumn} {
          flex-basis: ${(5 / 12) * 100}%;
        }
        ${RightColumn} {
          flex-basis: ${(7 / 12) * 100}%;
        }
      `};
    `};
`;

export const StyledCTA = styled((props) => <CTA {...props} />)``;

export const BrushElement = styled.img`
  width: 176px;
  height: 100px;
  margin-left: auto;
  margin-right: auto;
`;

export const StyledContainer = styled(Container)`
  padding: 0;

  ${breakpoint.desktop`
    padding: ${({ theme }) => `0 ${theme.spacing.standard}`};
  `};
`;

export const Wrapper = styled.div`
  overflow: hidden;
  background: ${({ theme }) => theme.palette.darkBackground};
  padding: ${({ theme }) => `${theme.spacing.medium} 0 0`};

  margin-bottom: ${({ $removeMarginBottom, theme }) =>
    $removeMarginBottom ? '0' : theme.spacing.large};
`;
